/**
 * Cart state.
 *
 * DESIGN: local-first, with a server mirror.
 *
 * A visitor must be able to fill a cart before they have an account — asking
 * someone to sign up before they can add a mango is how you lose the mango
 * sale. So the cart lives in localStorage and works signed-out.
 *
 * But `place_order()` reads from the `carts` table server-side (migration
 * 0600), because a client that submits its own line items is a client that
 * can submit its own prices. So once a user signs in, every mutation is
 * mirrored to the server, and the local cart merges up on sign-in.
 *
 * Prices are deliberately NOT stored here. The cart holds product IDs and
 * quantities; prices are read live from the catalog on render, and re-read
 * server-side at checkout. A price snapshotted into a cart is a price you'll
 * be honouring three weeks later.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '@/lib/supabase';

export interface CartLine {
  product_id: string;
  quantity: number;
  /** Display-only, so a cart badge can render before the catalog loads. */
  title: string;
  unit: string;
  image: string | null;
  added_at: string;
}

interface CartState {
  lines: CartLine[];
  isSyncing: boolean;

  add: (line: Omit<CartLine, 'added_at'>) => void;
  setQuantity: (productId: string, quantity: number) => void;
  remove: (productId: string) => void;
  clear: () => void;

  /** Push the whole local cart to the server. Call after sign-in. */
  syncToServer: () => Promise<void>;
  /** Replace local state from the server cart. Call on sign-in. */
  hydrateFromServer: () => Promise<void>;

  count: () => number;
  quantityOf: (productId: string) => number;
}

/**
 * Mirrors one line to the server, best-effort.
 *
 * Failures are swallowed to a console warning on purpose: a signed-out user
 * has no server cart and every call would 401, and a network blip must not
 * cost someone their cart. The local cart is the fallback, and `syncToServer`
 * reconciles at checkout.
 */
async function mirrorToServer(productId: string, quantity: number): Promise<void> {
  const { data: auth } = await supabase.auth.getSession();
  if (!auth.session) return;

  try {
    if (quantity <= 0) {
      const { data: cart } = await supabase
        .from('carts')
        .select('id')
        .eq('user_id', auth.session.user.id)
        .maybeSingle();

      if (cart) {
        await supabase
          .from('cart_items')
          .delete()
          .eq('cart_id', cart.id)
          .eq('product_id', productId);
      }
      return;
    }

    const { data: cart } = await supabase
      .from('carts')
      .upsert({ user_id: auth.session.user.id }, { onConflict: 'user_id' })
      .select('id')
      .single();

    if (!cart) return;

    // Absolute set, not the accumulating add_to_cart() RPC — this mirrors
    // local state, and local state is already the total.
    await supabase
      .from('cart_items')
      .upsert(
        { cart_id: cart.id, product_id: productId, quantity },
        { onConflict: 'cart_id,product_id' },
      );
  } catch (error) {
    console.warn('Cart mirror failed; local cart is still authoritative.', error);
  }
}

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      lines: [],
      isSyncing: false,

      add: (line) => {
        const existing = get().lines.find((l) => l.product_id === line.product_id);
        const nextQuantity = (existing?.quantity ?? 0) + line.quantity;

        set((state) => ({
          lines: existing
            ? state.lines.map((l) =>
                l.product_id === line.product_id ? { ...l, quantity: nextQuantity } : l,
              )
            : [...state.lines, { ...line, added_at: new Date().toISOString() }],
        }));

        void mirrorToServer(line.product_id, nextQuantity);
      },

      setQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().remove(productId);
          return;
        }
        set((state) => ({
          lines: state.lines.map((l) => (l.product_id === productId ? { ...l, quantity } : l)),
        }));
        void mirrorToServer(productId, quantity);
      },

      remove: (productId) => {
        set((state) => ({ lines: state.lines.filter((l) => l.product_id !== productId) }));
        void mirrorToServer(productId, 0);
      },

      clear: () => set({ lines: [] }),

      syncToServer: async () => {
        const { lines } = get();
        if (lines.length === 0) return;

        set({ isSyncing: true });
        try {
          await Promise.all(lines.map((l) => mirrorToServer(l.product_id, l.quantity)));
        } finally {
          set({ isSyncing: false });
        }
      },

      hydrateFromServer: async () => {
        const { data: auth } = await supabase.auth.getSession();
        if (!auth.session) return;

        set({ isSyncing: true });
        try {
          const { data } = await supabase
            .from('cart_items')
            .select('product_id, quantity, added_at, products(title, unit)')
            .order('added_at', { ascending: true });

          if (!data) return;

          const serverLines: CartLine[] = data.map((row) => {
            const product = row.products as unknown as { title: string; unit: string } | null;
            return {
              product_id: row.product_id,
              quantity: Number(row.quantity),
              title: product?.title ?? 'Item',
              unit: product?.unit ?? 'kg',
              image: null,
              added_at: row.added_at,
            };
          });

          // Union-merge, taking the larger quantity on conflict. Someone who
          // added 2kg on their phone and 1kg on their laptop meant to buy at
          // least 2kg — silently dropping to 1 would be the wrong guess.
          const merged = new Map<string, CartLine>();
          for (const line of serverLines) merged.set(line.product_id, line);
          for (const local of get().lines) {
            const existing = merged.get(local.product_id);
            merged.set(
              local.product_id,
              existing
                ? { ...existing, quantity: Math.max(existing.quantity, local.quantity) }
                : local,
            );
          }

          const lines = [...merged.values()];
          set({ lines });
          await Promise.all(lines.map((l) => mirrorToServer(l.product_id, l.quantity)));
        } catch (error) {
          console.warn('Could not load server cart; keeping the local one.', error);
        } finally {
          set({ isSyncing: false });
        }
      },

      count: () => get().lines.reduce((total, line) => total + line.quantity, 0),
      quantityOf: (productId) => get().lines.find((l) => l.product_id === productId)?.quantity ?? 0,
    }),
    {
      name: 'sreerasa-cart',
      storage: createJSONStorage(() => localStorage),
      // isSyncing is ephemeral — persisting it means a tab that closed
      // mid-sync reopens permanently "syncing".
      partialize: (state) => ({ lines: state.lines }),
      version: 1,
    },
  ),
);

/** Distinct item count, for the header badge. */
export function useCartCount(): number {
  return useCart((state) => state.lines.length);
}
